/**
 * Bot skill dial (PLAN §7). Accepts the bots' own presets and the contract's BOT_SKILL_PRESETS names.
 *
 * The sim contract's BotSkill has four numbers. The bots add a profile on top (BotSkillProfile) so
 * the presets differ in *how* they fail, not only how often:
 *   - reaction: a per-stimulus delay distribution (base + long-tailed jitter), not one fixed number;
 *   - misses: a chance to never handle a given mechanic instance at all (a failed dodge roll only
 *     means "late", see dodge.ts / duty.ts; `missChance` is the "never");
 *   - GCD slack: a delay between the GCD freeing up and the next press;
 *   - positioning: sloppy players stand in front of mobs, clump up and skip pre-spreading;
 *   - discipline: competent+ players save kicks for priority casts and only dispel what matters,
 *     novices kick whatever they see first (but late and less often) and dispel late.
 * A plain BotSkill object (custom dial) gets a profile interpolated from its reactionMs.
 */
import type { BotSkill, BotSkillPreset, Ms } from '@keystone/sim';
import { BOT_SKILL_PRESETS } from '@keystone/sim';

export type BotPresetName = 'novice' | 'competent' | 'expert';

export interface BotSkillProfile extends BotSkill {
  /** Mean of the extra per-stimulus reaction delay (exponential, capped at 3× the mean). */
  reactionJitterMs: Ms;
  /** Chance a mechanic instance (telegraph, zone, duty) is never handled. */
  missChance: number;
  /** Delay after the GCD/cast frees up before the next rotation press: uniform [lo, hi). */
  gcdSlackMs: readonly [Ms, Ms];
  /** Chance per think, while moving or repositioning, of simply not pressing anything. */
  moveIdleChance: number;
  /** 0 = sloppy (melee stand anywhere, ranged clump close, skip pre-spread), 1 = clean. */
  positioning: number;
  /** Save kicks for priority casts instead of kicking filler bolts. */
  kickDiscipline: boolean;
  /** Only dispel debuffs that matter (CC, jumps, real stacks, hurt holders). */
  dispelDiscipline: boolean;
  /** Extra delay before dispelling a debuff, on top of the reaction. */
  dispelDelayMs: Ms;
  /** Use the APL's finer conditions (aura-expiry, cap-avoidance lines). */
  aplFinesse: boolean;
  /** +grid: added to every "own health below" threshold (defensives and self-heals fire earlier). */
  hpBias?: number;
}

/**
 * +grid: Auto's wits (the Waystone Grid's Seasoned Instinct stars, BotOptions.wits): each point reacts
 * ~8% sooner, dodges and kicks a little more reliably, misses fewer mechanics and uses its defensives
 * and self-heals 4% of health earlier. 0 = the profile unchanged.
 */
export function witsSkill(skill: BotSkillProfile, wits: number): BotSkillProfile {
  const w = Math.max(0, Math.min(5, Number.isFinite(wits) ? wits : 0));
  if (w === 0) return skill;
  const quick = Math.max(0.6, 1 - 0.08 * w);
  return {
    ...skill,
    reactionMs: Math.round(skill.reactionMs * quick),
    reactionJitterMs: Math.round(skill.reactionJitterMs * quick),
    dodgeReliability: Math.min(0.99, skill.dodgeReliability + 0.04 * w),
    kickReliability: Math.min(0.99, skill.kickReliability + 0.03 * w),
    missChance: skill.missChance * Math.max(0.4, 1 - 0.15 * w),
    hpBias: (skill.hpBias ?? 0) + 0.04 * w,
  };
}

/** Bots-side override of BOT_SKILL_PRESETS.novice.dodgeReliability (0.5). */
const NOVICE_DODGE = 0.65;

const PROFILE: Readonly<Record<BotPresetName, Omit<BotSkillProfile, keyof BotSkill>>> = {
  novice: {
    reactionJitterMs: 300,
    missChance: 0.08,
    gcdSlackMs: [120, 520],
    moveIdleChance: 0.5,
    positioning: 0,
    kickDiscipline: false,
    dispelDiscipline: false,
    dispelDelayMs: 1500,
    aplFinesse: false,
  },
  competent: {
    reactionJitterMs: 120,
    missChance: 0.03,
    gcdSlackMs: [0, 40],
    moveIdleChance: 0,
    positioning: 1,
    kickDiscipline: true,
    dispelDiscipline: true,
    dispelDelayMs: 0,
    aplFinesse: true,
  },
  expert: {
    reactionJitterMs: 50,
    missChance: 0.005,
    gcdSlackMs: [0, 40],
    moveIdleChance: 0,
    positioning: 1,
    kickDiscipline: true,
    dispelDiscipline: true,
    dispelDelayMs: 0,
    aplFinesse: true,
  },
};

export const BOT_PRESETS: Readonly<Record<BotPresetName, BotSkillProfile>> = {
  // Novice dodges a bit more than the contract's 0.5: with the +10 failure tax on dodgeable hits,
  // a coin-flip dodge (never moving for half the telegraphs) wiped boss after boss until the run cap.
  novice: { ...BOT_SKILL_PRESETS.novice, dodgeReliability: NOVICE_DODGE, ...PROFILE.novice },
  competent: { ...BOT_SKILL_PRESETS.competent, ...PROFILE.competent },
  expert: { ...BOT_SKILL_PRESETS.expert, ...PROFILE.expert },
};

export type SkillInput = BotSkill | BotPresetName | BotSkillPreset;

export const DEFAULT_PRESET: BotPresetName = 'competent';

/** Contract preset names that map onto a bots preset profile. */
const ALIAS: Partial<Record<BotSkillPreset, BotPresetName>> = { average: 'competent', skilled: 'competent', elite: 'expert' };

function isProfile(s: BotSkill): s is BotSkillProfile {
  return typeof (s as Partial<BotSkillProfile>).missChance === 'number';
}

/**
 * Profile for a bare BotSkill (an explicit custom dial): no extra reaction jitter or GCD slack, so
 * its timing and positioning stay exactly what it says; the miss chance and discipline follow its
 * reliabilities (dodge 0.93 → none, 0.5 → the novice profile).
 */
export function profileFor(skill: BotSkill): BotSkillProfile {
  if (isProfile(skill)) return skill;
  const lo = BOT_PRESETS.expert;
  const hi = BOT_PRESETS.novice;
  const t = Math.min(1, Math.max(0, (lo.dodgeReliability - skill.dodgeReliability) / (lo.dodgeReliability - hi.dodgeReliability)));
  const sloppy = t >= 0.75;
  return {
    ...skill,
    reactionJitterMs: 0,
    missChance: hi.missChance * t,
    gcdSlackMs: [0, 0],
    moveIdleChance: 0,
    positioning: 1,
    kickDiscipline: !sloppy,
    dispelDiscipline: !sloppy,
    dispelDelayMs: 0,
    aplFinesse: !sloppy,
  };
}

/**
 * Learning a boss (wipe-loop safety): after each wipe on the same boss the group plays that fight
 * a bit better: misses, reaction jitter, dodge/kick failures and GCD slack shrink toward the
 * competent profile, LEARN_PER_WIPE of the gap per wipe (capped at LEARN_MAX). Positioning and
 * discipline habits don't change. A novice group that keeps dying to a boss still pays in timer
 * (each wipe costs the run back and the death penalties) but converges on a kill instead of
 * looping wipes until the run cap. Never makes a bot worse; a competent+ bot is unaffected.
 */
export const LEARN_PER_WIPE = 0.25;
export const LEARN_MAX = 0.85;

export function learnedSkill(base: BotSkillProfile, wipes: number): BotSkillProfile {
  if (wipes <= 0) return base;
  const t = Math.min(LEARN_MAX, wipes * LEARN_PER_WIPE);
  const to = BOT_PRESETS.competent;
  const down = (a: number, b: number): number => (a > b ? a + (b - a) * t : a);
  const up = (a: number, b: number): number => (a < b ? a + (b - a) * t : a);
  return {
    ...base,
    reactionMs: down(base.reactionMs, to.reactionMs),
    reactionJitterMs: down(base.reactionJitterMs, to.reactionJitterMs),
    missChance: down(base.missChance, to.missChance),
    dodgeReliability: up(base.dodgeReliability, to.dodgeReliability),
    kickReliability: up(base.kickReliability, to.kickReliability),
    gcdSlackMs: [down(base.gcdSlackMs[0], to.gcdSlackMs[0]), down(base.gcdSlackMs[1], to.gcdSlackMs[1])],
    moveIdleChance: down(base.moveIdleChance, to.moveIdleChance),
  };
}

export function resolveSkill(input: SkillInput | undefined): BotSkillProfile {
  if (input === undefined) return BOT_PRESETS[DEFAULT_PRESET];
  if (typeof input === 'object') return profileFor(input);
  if (input in BOT_PRESETS) return BOT_PRESETS[input as BotPresetName];
  const alias = ALIAS[input as BotSkillPreset];
  if (alias) return { ...BOT_PRESETS[alias], ...BOT_SKILL_PRESETS[input as BotSkillPreset] };
  return profileFor(BOT_SKILL_PRESETS[input as BotSkillPreset] ?? BOT_PRESETS[DEFAULT_PRESET]);
}
